const destinations = [
  {
    name: "Caribbean & Bahamas",
    context: "Nassau, the Exumas, and the Abacos are the top boating and fishing destinations for Upper Grand Lagoon residents. The Bahamas are accessible by boat from the Florida Panhandle — and many local anglers and charter crews make the crossing during tournament season",
    urgency: "Bahamas crossings require valid passports for every crew member. A passport discovered expired during tournament prep can disqualify a team — we have processed renewals on 48-hour timelines for local fishing crews",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico & Gulf Ports",
    context: "Cancun, Cozumel, and Playa del Carmen draw Panama City Beach families and spring breakers. The Gulf of Mexico connects the Panhandle to Mexican ports — and some fishing tournaments include Mexican waters in their range",
    urgency: "Spring break bookings from Gulf Coast families peak in January — standard processing cannot accommodate March departure dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Japan & Pacific (Military PCS)",
    context: "Kadena Air Base, Yokota Air Base, and Misawa Air Base are common Pacific assignments for Tyndall AFB personnel. The F-22 training pipeline at Tyndall prepares pilots for Pacific Air Forces deployments and permanent changes of station",
    urgency: "Pacific PCS orders from Tyndall often arrive with 30–45 days to report — dependent passports must be current before the household goods shipper arrives",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Europe (Military & NATO)",
    context: "RAF Lakenheath, Spangdahlem Air Base, and Ramstein Air Base are frequent European assignments for Tyndall-trained personnel. NATO training exchanges and partnership programs also generate European travel from the Panhandle",
    urgency: "European PCS and TDY orders have immovable report dates. Family passports must be current before the official travel date is set — not after",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Toronto and Montreal attract Panama City Beach families for summer lake vacations and fishing trips. The Canadian border requires passports for all U.S. air travelers — and many Panhandle residents discover this only when booking",
    urgency: "Summer Canada trips are often booked in April and May — with passport checks that happen too late for standard processing to meet July departure dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom",
    context: "London and Edinburgh draw Panama City Beach students on study-abroad programs and military families visiting UK-based relatives. The Florida State University Panama City campus sends students to international programs that require current passports",
    urgency: "Study-abroad program deadlines fall in October and February — but students often do not check their passport until after acceptance, creating an emergency timeline",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "55%", label: "of Upper Grand Lagoon expedited requests involve military families from Tyndall AFB, fishing crews, or seasonal hospitality workers with fixed travel windows" },
  { stat: "290 Miles", label: "one-way distance to the Atlanta Passport Agency — the nearest public option for Bay County residents" },
  { stat: "Mar–Apr", label: "peak Spring Break season when the local economy peaks — and when passport discoveries spike for seasonal workers" },
  { stat: "48 Hours", label: "shortest fishing tournament emergency we have accommodated for a Panama City Beach charter crew" },
];

export default function UpperGrandLagoonDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Upper Grand Lagoon Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Upper Grand Lagoon's travel profile is shaped by military assignments from Tyndall AFB, offshore fishing tournaments, Spring Break seasonal work, and the reality of being 290 miles from the nearest passport agency. Understanding these patterns helps us serve Bay County residents with the urgency their timelines demand.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across the Florida Panhandle and all of Florida with the same expedited passport expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/panama-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Panama City <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/pensacola" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Pensacola <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/tallahassee" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tallahassee <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}